/**
 * #765 C22 i18n framework V1 — supported locales + config.
 *
 * V1 launch: es-DO (default RD), es-MX (Mexico), es-CO (Colombia), en-US.
 * V2 roadmap: pt-BR + es-419 universal fallback.
 *
 * Decision PO Gabriel Brens 2026-06-04.
 */

export const SUPPORTED_LOCALES = [
  'es-DO',
  'es-MX',
  'es-CO',
  'en-US',
] as const;

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: SupportedLocale = 'es-DO';

export const LOCALE_LABELS: Record<SupportedLocale, string> = {
  'es-DO': 'Español (RD)',
  'es-MX': 'Español (México)',
  'es-CO': 'Español (Colombia)',
  'en-US': 'English (US)',
};

export const LOCALE_FLAGS: Record<SupportedLocale, string> = {
  'es-DO': 'DO',
  'es-MX': 'MX',
  'es-CO': 'CO',
  'en-US': 'US',
};

const LOCAL_STORAGE_KEY = 'inbomarket_locale';

export function detectBrowserLocale(): SupportedLocale {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;

  try {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (stored && SUPPORTED_LOCALES.includes(stored as SupportedLocale)) {
      return stored as SupportedLocale;
    }
  } catch {
    // ignore localStorage errors
  }

  const browserLang = navigator?.language;
  if (browserLang) {
    if (SUPPORTED_LOCALES.includes(browserLang as SupportedLocale)) {
      return browserLang as SupportedLocale;
    }
    const baseLang = browserLang.split('-')[0];
    const fallback = SUPPORTED_LOCALES.find((l) => l.startsWith(baseLang));
    if (fallback) return fallback;
  }

  return DEFAULT_LOCALE;
}

export function persistLocale(locale: SupportedLocale): void {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, locale);
  } catch {
    // ignore
  }
}
